import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Volume2, Target, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Tests Menu Page - iOS style
 * Navigation hub for all test types
 */
const Tests = ({ t }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium' && user?.role !== 'admin';

  const testItems = [
  {
    id: 'audiometric',
    icon: Volume2,
    title: t?.audiometricTest || 'Explorer mon écoute',
    description: t?.audiometricTestDesc || 'Explorez votre écoute et laissez Hearen s’adapter progressivement à votre perception.',
    path: '/hearing-knowledge',
    free: false,
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  
  {
    id: 'tinnitus',
    icon: Target,
    title: t?.tinnitusTest || 'Explorer mes acouphènes',
    description: t?.tinnitusTestDesc || 'Identifiez la tonalité qui se rapproche le plus de votre perception des acouphènes pour personnaliser votre environnement sonore.',
    path: '/tinnitus-test',
    free: false,
    image: 'https://images.unsplash.com/photo-1617994452722-4145e196248b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxmcmVxdWVuY3l8ZW58MHx8fHwxNzYyNzg4MjE4fDA&ixlib=rb-4.1.0&q=85',
  },
];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pb-40 pt-20 overflow-y-auto">
      {/* Main content - Scrollable cards with background images */}
      <div className="max-w-4xl mx-auto px-4 space-y-4">
        
        {/* Test cards */}
        {testItems.map((item) => {
          const Icon = item.icon;
          const locked = isFreeUser && !item.free;
          return (
            <button
              key={item.id}
              onClick={() => locked ? navigate('/premium') : navigate(item.path)}
              data-testid={`test-card-${item.id}`}
              className={`w-full h-48 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98] relative group ${locked ? 'opacity-70' : ''}`}
              style={{
                backgroundImage: `linear-gradient(rgba(0, 0, 0, ${locked ? '0.6' : '0.4'}), rgba(0, 0, 0, ${locked ? '0.75' : '0.6'})), url(${item.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Lock badge for premium-only tests */}
              {locked && (
                <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-amber-500/90 backdrop-blur-sm px-3 py-1.5 rounded-full z-10">
                  <Lock size={14} className="text-white" />
                  <span className="text-xs font-semibold text-white">Premium</span>
                </div>
              )}

              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col justify-end p-6 text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <Icon size={24} className="text-white" strokeWidth={2.5} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      {item.title}
                    </h3>
                    <p className="text-sm text-white/90">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Hover effect */}
              <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/10 transition-all"></div>
            </button>
          );
        })}

        {/* Single access card to History */}
        <button
          onClick={() => navigate('/history')}
          data-testid="tests-history-access"
          className="w-full rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98] relative group text-left"
          style={{
            background: 'linear-gradient(135deg, rgba(15,23,42,0.96) 0%, rgba(30,41,59,0.94) 100%)',
          }}
        >
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {t?.historyAccessTitle || 'Accès à mon historique'}
                </h3>
                <p className="text-sm text-white/80 mt-1">
                  {t?.historyAccessDesc || 'Retrouvez vos résultats, graphiques et leur évolution dans le temps.'}
                </p>
              </div>

              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xl">📈</span>
              </div>
            </div>

            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-white/90 text-sm font-medium">
              {t?.viewHistory || 'Voir mon historique'}
            </div>
          </div>

          <div className="absolute inset-0 bg-blue-500/0 group-hover:bg-blue-500/5 transition-all"></div>
        </button>

        {/* Info card */}
        <div className="mt-6 p-4 bg-blue-50 rounded-2xl border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>💡 Info:</strong> {t?.testsInfo || 'Ces étapes aident Hearen à personnaliser votre environnement sonore.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Tests;